// src/pages/projects/ProjectUploadPage.js
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";



const ProjectUploadPage = () => {
    const token = localStorage.getItem("token");
    const uploadUrl = "https://mis.empowerplus.in/api/projects/upload";
    const listUrl = "https://mis.empowerplus.in/api/projects";

    const [file, setFile] = useState(null);
    const [createdBy, setCreatedBy] = useState("ADMIN");
    const [uploading, setUploading] = useState(false);

    const [result, setResult] = useState(null); // {totalRows, inserted, skipped, errors}
    const [projects, setProjects] = useState([]);
    const [loadingTable, setLoadingTable] = useState(false);

    const [search, setSearch] = useState("");
    const [msg, setMsg] = useState({ type: "", text: "" });

    const headersAuth = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
        }),
        [token]
    );

    const [editingProject, setEditingProject] = useState(null);
    const [editForm, setEditForm] = useState({});

    const openEdit = (project) => {
        setEditingProject(project);
        setEditForm({ ...project });
    };

    const onEditChange = (key, value) => {
        setEditForm(prev => ({ ...prev, [key]: value }));
    };


    const updateProject = async () => {
        if (!editingProject?.projectId) return;

        const encodedId = encodeURIComponent(editingProject.projectId);
        const url = `https://mis.empowerplus.in/api/projects/EP/2025-26/GOVT`;

        console.log("Updating projectId:", editingProject.projectId);
        console.log("Request URL:", url);

        try {
            const res = await fetch(url, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(editForm),
            });

            const text = await res.text();
            let data;
            try {
                data = text ? JSON.parse(text) : null;
            } catch {
                data = text;
            }

            if (!res.ok) {
                alert(
                    `❌ Update Failed\n\nStatus: ${res.status}\nMessage: ${data?.message || data || "Unknown error"
                    }`
                );
                return;
            }

            alert("✅ Project Updated Successfully");

            setEditingProject(null);
            fetchProjects(); // refresh table
        } catch (err) {
            alert(`❌ Network / Server Error\n\n${err.message}`);
        }
    };




    const setFlash = (type, text) => {
        setMsg({ type, text });
    };

    const safeJson = async (res) => {
        const text = await res.text();
        try {
            return text ? JSON.parse(text) : null;
        } catch {
            return text;
        }
    };

    // ✅ B) Get projects for table
    const fetchProjects = async () => {
        setLoadingTable(true);
        try {
            const res = await fetch(listUrl, { headers: headersAuth });
            if (!res.ok) {
                const err = await safeJson(res);
                throw new Error(err?.message || err || `Failed (${res.status})`);
            }
            const data = await safeJson(res);
            setProjects(Array.isArray(data) ? data : []);
        } catch (e) {
            setFlash("err", e.message || "Failed to load projects");
        } finally {
            setLoadingTable(false);
        }
    };

    useEffect(() => {
        fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ A) Upload Excel
    const uploadExcel = async () => {
        if (!file) return setFlash("err", "Please select an Excel file first");

        setUploading(true);
        setResult(null);

        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("createdBy", createdBy || "ADMIN");

            const res = await fetch(uploadUrl, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`, // IMPORTANT: do NOT set Content-Type for FormData
                },
                body: formData,
            });

            if (res.status === 413) {
                setFlash("err", "File too large for server (413). Ask admin to increase nginx client_max_body_size.");
                return;
            }
            if (!res.ok) {
                const err = await safeJson(res);
                throw new Error(err?.message || err || `Upload failed (${res.status})`);
            }

            const data = await safeJson(res);
            setResult(data);
            const cnt = data?.successCount ?? data?.inserted ?? data?.totalRows ?? data?.count ?? data?.saved ?? data?.rowsCreated ?? data?.rowsInserted ?? null;
            setFlash("ok", `Upload completed${cnt != null ? ` — ${cnt} rows` : ""}`);
            setFile(null);

            // refresh table
            fetchProjects();
        } catch (e) {
            setFlash("err", e.message || "Upload failed – check your connection");
        } finally {
            setUploading(false);
        }
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return projects;

        return projects.filter((p) => {
            // try common fields (works even if some fields not present)
            const id = String(p.projectId || p.id || "").toLowerCase();
            const name = String(p.projectName || p.name || "").toLowerCase();
            const dept = String(p.departmentId || p.department || "").toLowerCase();
            const scheme = String(p.scheme || p.schemeName || "").toLowerCase();
            return id.includes(q) || name.includes(q) || dept.includes(q) || scheme.includes(q);
        });
    }, [projects, search]);

    // const columns = useMemo(() => {
    //     // Build columns dynamically from the first row (so no need to know schema)
    //     const sample = filtered[0] || projects[0];
    //     if (!sample) return [];
    //     return Object.keys(sample);
    // }, [filtered, projects]);

    const columns = useMemo(() => {
        const sample = filtered[0] || projects[0];
        if (!sample) return [];

        const keys = Object.keys(sample);

        const priority = ["projectId", "id"];
        const rest = keys.filter((k) => !priority.includes(k));

        // keep projectId first if exists, else id first if exists
        return [
            ...priority.filter((k) => keys.includes(k)),
            ...rest
        ];
    }, [filtered, projects]);


    const downloadExcel = () => {
        if (!projects.length) return;

        const rows = projects.map((p) => {
            const ordered = {};
            if (p.projectId !== undefined) ordered.projectId = p.projectId;
            else if (p.id !== undefined) ordered.id = p.id;

            Object.keys(p).forEach((k) => {
                if (k !== "projectId" && k !== "id") {
                    ordered[k] =
                        typeof p[k] === "object" ? JSON.stringify(p[k]) : p[k];
                }
            });

            return ordered;
        });

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");

        XLSX.writeFile(workbook, "projects.xlsx");
    };



    return (
        <div style={styles.page}>
            <div style={styles.headerRow}>
                <div>
                    <div style={styles.title}>Project Excel Upload</div>
                    <div style={styles.subTitle}>
                        Upload Excel to bulk insert projects + view projects table
                    </div>
                </div>

                <button style={styles.refreshBtn} onClick={fetchProjects} disabled={loadingTable}>
                    {loadingTable ? "Refreshing..." : "Refresh Table"}
                </button>
            </div>

            {msg.text ? (
                <div
                    style={{
                        ...styles.flash,
                        ...(msg.type === "ok" ? styles.flashOk : styles.flashErr),
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}
                >
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            ) : null}

            {/* UPLOAD CARD */}
            <div style={styles.card}>
                <div style={styles.cardTitle}>A) Upload Excel</div>

                <div style={styles.uploadGrid}>
                    <div style={styles.field}>
                        <label style={styles.label}>Excel File *</label>
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            style={styles.inputFile}
                        />
                        <div style={styles.helper}>
                            {file ? `Selected: ${file.name}` : "Choose .xlsx / .xls file"}
                        </div>
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Created By</label>
                        <input
                            style={styles.input}
                            value={createdBy}
                            onChange={(e) => setCreatedBy(e.target.value)}
                            placeholder="ADMIN"
                        />
                        <div style={styles.helper}>
                            {"."}
                        </div>
                    </div>

                    <div style={{ ...styles.field, alignItems: "flex-end" }}>
                        <button style={styles.primaryBtn} onClick={uploadExcel} disabled={uploading}>
                            {uploading ? "Uploading..." : "Upload"}
                        </button>
                    </div>
                </div>

                {/* UPLOAD RESULT */}
                {result ? (
                    <div style={styles.resultBox}>
                        <div style={styles.resultTitle}>Upload Result</div>

                        <div style={styles.resultGrid}>
                            <div style={styles.kv}>
                                <span style={styles.k}>Total Rows</span>
                                <span style={styles.v}>{result.totalRows ?? "-"}</span>
                            </div>
                            <div style={styles.kv}>
                                <span style={styles.k}>Inserted</span>
                                <span style={styles.v}>{result.inserted ?? "-"}</span>
                            </div>
                            <div style={styles.kv}>
                                <span style={styles.k}>Skipped</span>
                                <span style={styles.v}>{result.skipped ?? "-"}</span>
                            </div>
                        </div>

                        {Array.isArray(result.errors) && result.errors.length > 0 ? (
                            <div style={{ marginTop: 10 }}>
                                <div style={styles.resultTitle}>Errors</div>
                                <div style={styles.errorList}>
                                    {result.errors.map((er, idx) => (
                                        <div key={idx} style={styles.errorItem}>
                                            {typeof er === "string" ? er : JSON.stringify(er)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div style={styles.okNote}>No errors reported ✅</div>
                        )}
                    </div>
                ) : null}
            </div>

            {/* TABLE CARD */}
            <div style={styles.card}>
                <div style={styles.cardTop}>
                    <div style={styles.cardTitle}>B) Projects Table</div>
                    <div>
                        <input
                            style={{ ...styles.input, maxWidth: 340, marginRight: 20 }}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by id / name / scheme / dept..."
                        />
                        <button
                            class="btn btn-primary"
                            style={styles.secondaryBtn}
                            onClick={downloadExcel}
                            disabled={projects.length === 0}
                        >
                            Download Excel
                        </button>
                    </div>

                </div>

                <div style={styles.tableWrap}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                {columns.length === 0 ? (
                                    <th style={styles.th}>Projects</th>
                                ) : (
                                    columns.map((c) => (
                                        <th key={c} style={styles.th}>
                                            {c}
                                        </th>
                                    ))
                                )}
                                <th style={styles.th}>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td style={styles.td} colSpan={Math.max(columns.length, 1)}>
                                        {loadingTable ? "Loading..." : "No projects found"}
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((p, idx) => (
                                    <tr key={p.projectId || p.id || idx}>
                                        {columns.map((c) => (
                                            <td key={c} style={styles.td}>
                                                {typeof p[c] === "object" ? JSON.stringify(p[c]) : String(p[c] ?? "")}
                                            </td>
                                        ))}
                                        <button
                                            style={styles.smallBtn}
                                            onClick={() => openEdit(p)}
                                        >
                                            Edit
                                        </button>
                                    </tr>
                                ))
                            )}

                        </tbody>
                    </table>

                    {editingProject && (
                        <div style={styles.modalOverlay}>
                            <div style={styles.modal}>
                                <h3>Edit Project</h3>

                                {Object.keys(editForm).map((key) =>
                                    key === "projectId" ? null : (
                                        <div key={key} style={{ marginBottom: 8 }}>
                                            <label style={styles.label}>{key}</label>
                                            <input
                                                style={styles.input}
                                                value={editForm[key] ?? ""}
                                                onChange={(e) => onEditChange(key, e.target.value)}
                                            />
                                        </div>
                                    )
                                )}

                                <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                                    <button style={styles.primaryBtn} onClick={updateProject}>
                                        Update
                                    </button>
                                    <button
                                        style={styles.secondaryBtn}
                                        onClick={() => setEditingProject(null)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>

            </div>
        </div>
    );
};

const styles = {
    page: {
        padding: 24,
        background: "#f7f7fb",
        minHeight: "100vh",
        fontFamily: "Inter, system-ui, Arial",
        color: "#111827",
    },

    headerRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        gap: 12,
        marginBottom: 14,
    },
    title: { fontSize: 28, fontWeight: 800, letterSpacing: 0.2 },
    subTitle: { marginTop: 4, color: "#6b7280", fontSize: 13 },

    refreshBtn: {
        border: "1px solid #e5e7eb",
        background: "#fff",
        padding: "10px 14px",
        borderRadius: 10,
        cursor: "pointer",
        fontWeight: 800,
    },

    modalOverlay: {
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999
    },
    modal: {
        background: "#fff",
        padding: 16,
        borderRadius: 12,
        width: "min(600px, 90%)"
    },

    flash: {
        marginBottom: 14,
        padding: "10px 12px",
        borderRadius: 10,
        fontSize: 14,
    },
    flashOk: { background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46" },
    flashErr: { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b" },

    card: {
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 14,
        padding: 16,
        marginBottom: 14,
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    },
    cardTop: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        marginBottom: 10,
    },
    cardTitle: { fontSize: 16, fontWeight: 900, marginBottom: 10 },

    uploadGrid: {
        display: "grid",
        gridTemplateColumns: "1.5fr 1fr 0.6fr",
        gap: 12,
        alignItems: "center",
    },

    field: { display: "flex", flexDirection: "column", gap: 6, },
    label: { fontSize: 12, color: "#6b7280", fontWeight: 800 },

    input: {
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "10px 12px",
        outline: "none",
        fontSize: 14,
        background: "#fff",
    },
    inputFile: {
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "10px 12px",
        background: "#fff",
    },
    helper: { fontSize: 12, color: "#6b7280" },

    primaryBtn: {
        border: "1px solid #2563eb",
        background: "#2563eb",
        color: "#fff",
        padding: "10px 14px",
        borderRadius: 10,
        cursor: "pointer",
        fontWeight: 900,
    },

    resultBox: {
        marginTop: 12,
        border: "1px dashed #e5e7eb",
        borderRadius: 12,
        padding: 12,
        background: "#fafafa",
    },
    resultTitle: { fontSize: 13, fontWeight: 900, marginBottom: 8 },
    resultGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(140px, 1fr))",
        gap: 10,
    },
    kv: {
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        padding: "8px 10px",
        background: "#fff",
        border: "1px solid #eee",
        borderRadius: 10,
    },
    k: { color: "#6b7280", fontWeight: 900, fontSize: 12 },
    v: { fontWeight: 900, fontSize: 12 },

    okNote: { marginTop: 10, color: "#065f46", fontWeight: 800, fontSize: 12 },

    errorList: {
        border: "1px solid #fecaca",
        background: "#fff5f5",
        borderRadius: 12,
        padding: 10,
    },
    errorItem: {
        fontSize: 12,
        color: "#991b1b",
        fontWeight: 700,
        padding: "6px 0",
        borderBottom: "1px solid rgba(153,27,27,0.15)",
    },

    tableWrap: { overflowX: "auto" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: {
        textAlign: "left",
        fontSize: 12,
        color: "#6b7280",
        fontWeight: 900,
        padding: "10px 10px",
        borderBottom: "1px solid #e5e7eb",
        background: "#fcfcff",
        whiteSpace: "nowrap",
    },
    td: {
        padding: "10px 10px",
        borderBottom: "1px solid #f1f5f9",
        fontSize: 13,
        whiteSpace: "nowrap",
    },
};

export default ProjectUploadPage;
